import React from 'react';
import Layout from '../../components/global/layout';
import SEO from '../../components/seo';
import PageBanner from '../../components/layouts/page-banner';
import {
  Box,
  Container,
  Grid,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import LightMode from '../../components/global/light-mode';
import VerticalPadding from '../../components/global/vertical-padding';
import { SiteImage } from '../../components/site-image';
import headquartersImage from '../../images/canopygrowth-headquarters.jpg';
import CGLogo from '../../images/svg/canopygrowth-logo-colour.svg';

const useCaseStyles = makeStyles((theme) => ({
  root: {
    '& p': {
      lineHeight: 1.5,
    },
  },
  img: {
    height: 'auto',
    marginTop: 50,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      maxWidth: 250,
    },
    [theme.breakpoints.only('lg')]: {
      maxWidth: 300,
    },
  },
}));

const CanopyGrowth: React.FC = () => {
  const useCaseStyle = useCaseStyles();

  return (
    <Layout>
      <SEO
        title="Canopy Growth Takes Development Further"
        description="Canopy Growth relies on Marvel &amp; Snap to strengthen development processes, shorten development cycles, assist with maintenance, and enable continuous delivery of web services."
      />
      <PageBanner
        heading="Canopy Growth Takes Development Further"
        maxWidth={1200}
      />
      <LightMode>
        <VerticalPadding>
          <Container style={{ overflow: 'hidden' }}>
            <Grid container spacing={5} justifyContent="space-between">
              <Grid item xs={12} md={6} lg={5} style={{ textAlign: 'center' }}>
                <img
                  src={CGLogo}
                  alt="Canopy Growth logo"
                  width="400"
                  height="263"
                  className={useCaseStyle.img}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  component="p"
                  className="lead"
                  gutterBottom={true}
                >
                  For{' '}
                  <Link
                    href="https://www.canopygrowth.com"
                    target="blank"
                    rel="noopener"
                  >
                    Canopy Growth Corporation
                  </Link>{' '}
                  websites are the storefronts of an international e-commerce
                  business. In addition to providing an online presence in a
                  variety of markets, the company’s multiple sites have to
                  deliver an optimal user experience by positioning in-depth
                  product information and enabling online sales, while at the
                  same time conforming to regulatory restrictions in each
                  market. Canopy Growth relies on Marvel &amp; Snap to
                  strengthen development processes, shorten development cycles,
                  assist with maintenance, and enable continuous delivery of web
                  services.
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 40 }}
                >
                  Challenge
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    Canopy Growth is a world-leading diversified cannabis and
                    cannabinoid-based consumer product company. From its
                    headquarters in Canada, the company works within a variety
                    of regulatory frameworks to market and sell to customers
                    across North America, in Europe, the United Kingdom, and
                    Africa. In each market, the company’s websites are
                    continuously evolving e-commerce portals that must conform
                    to challenging marketing, sales, regulatory, and consumer
                    requirements.
                  </p>
                  <p>
                    “We have nine websites to address our various markets,”
                    explains Paul Nesbit, director of application services at
                    Canopy Growth. “There are really two aspects to managing
                    these sites. At the high level, there is the standard
                    front-end web operations that most websites require to
                    ensure the sites continue to represent the brand. The more
                    challenging requirement is maintaining and supporting the
                    back-end e-commerce features.
                  </p>
                  <p>
                    “We have a team of people in-house that is responsible for
                    managing and maintaining those functions and applying
                    website changes. Those changes can come from a variety of
                    areas. Maintenance and support channels, like customer care,
                    might request functional changes that are needed because a
                    customer has reported that something isn’t working on a
                    site, for example. Or the product team might need us to add
                    a new product.
                  </p>
                  <p>
                    “Because we work in a regulated industry and the regulations
                    are different from one market to the next, there is quite a
                    bit of software development work needed to apply the changes
                    on each site in a way that conforms to regulations that
                    outline how products are presented and sold in each market.
                    We work really closely with our legal team to ensure that
                    everything we do complies. But we have to always do that
                    with the optimal user experience top-of-mind.”
                  </p>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
      <Box bgcolor="primary.main" textAlign="center">
        <VerticalPadding>
          <Container>
            <Grid container justifyContent="center">
              <Grid item xs={12} lg={11} xl={10} style={{ color: 'white' }}>
                <Typography
                  variant="h2"
                  component="blockquote"
                  style={{ fontFamily: 'Visby CF', textTransform: 'unset' }}
                >
                  &ldquo;I never feel like we are being billed for more than
                  what has been actually consumed and I never feel like
                  something is taking longer or costing more than it should.
                  Which is kind of a rare thing.&rdquo;
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </Box>
      <LightMode>
        <VerticalPadding>
          <Container style={{ lineHeight: 1.77, overflow: 'hidden' }}>
            <Grid container spacing={5} justifyContent="space-between">
              <Grid item xs={12} md={6} lg={5}>
                <SiteImage
                  src={headquartersImage}
                  objectFit="cover"
                  alt="Canopy Growth Headquarters"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h3" component="h2">
                  Solution
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    Early in the evolution of its e-commerce business, Canopy
                    Growth was looking for a supplier to provide the support it
                    needed to address the company’s ongoing and increasing web
                    development requirements. Marvel &amp; Snap was recommended
                    and has been working with Paul and his team ever since.
                  </p>
                  <p>
                    “Because of the volume of work needed to keep our sites
                    running properly, we have to have multiple teams working on
                    different layers at the same time,” Paul explains. “And we
                    have to scale efforts up and down to deal with demand. We
                    have vendors we rely on to provide that additional capacity
                    when we need it and we use Marvel &amp; Snap on specific
                    types of sites.
                  </p>
                  <p>
                    “In addition to support and maintenance for our WordPress
                    sites, Marvel &amp; Snap provides software operational
                    support and development for our Shopify e-commerce
                    services,” Paul says. “So, if there’s new functionality or
                    features needed for a WordPress site, we often assign that
                    to Marvel &amp; Snap. And if there’s development needed for
                    a new feature or changes to an existing feature on our
                    Shopify sites, that again is something we feel comfortable
                    assigning to Marvel &amp; Snap.
                  </p>
                  <p>
                    “It’s a very integrated process. We have them on retainer
                    and our web development lead assigns tasks as they come up,
                    which could be bug fixes, enhancements, new features, or
                    simple user story changes. And there’s a constant flow of
                    these assignments.”
                  </p>
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 40 }}
                >
                  Result
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    Paul explains that with Marvel &amp; Snap working as an
                    extension of his in-house team, Canopy Growth has access to
                    a more cost-effective approach to software operational
                    support.
                  </p>
                  <p>
                    “Having Marvel &amp; Snap allows us to do more when it comes
                    to development. They are always very responsive. They don’t
                    need a large degree of explaining or hand-holding, which
                    means, I think on the whole, that their developers are very
                    strong.
                  </p>
                  <p>
                    “We’re very happy with the quality of the work because we
                    rarely have to iterate on assignments. If they’re not clear
                    about something, they don’t generally proceed. They will get
                    clarity on specifically what it is we are looking for, and
                    more often than not they get it right the first time around.
                    They deliver functional code that does what is expected. So,
                    it’s really been minimal friction from assignment to
                    development to deployment.
                  </p>
                  <p>
                    “They also have a very customer-friendly business model with
                    very competitive rates. And I think they are always fair. I
                    never feel like we are being billed for more than what has
                    been actually consumed and I never feel like something is
                    taking longer or costing more than it should. Which is kind
                    of a rare thing.”
                  </p>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
    </Layout>
  );
};

export default CanopyGrowth;
